/**
 * SDR Stock Manager - Supabase Remote Procedure Calls (RPC)
 * @module services/rpc
 */

import { supabaseRpc, toInventoryRow } from './supabase.js';
import { getAuthenticatedOperatorEmail } from './auth.js';
import { normalizeBoxContents } from '../utils/box-contents.js';

/**
 * Invokes the atomic `borrow_inventory` RPC procedure.
 * @param {Array<{ prod?: any, qty?: number, key?: string, name?: string, quantity?: number, selectedBoxes?: Array<{ itemId: string, boxIndex: number }> }>} itemsToBorrow
 * @param {string} [purpose='']
 * @param {string} [operatorEmail='']
 * @param {string} [borrowerName='']
 * @returns {Promise<any>}
 */
export async function rpcBorrowInventory(itemsToBorrow, purpose = '', operatorEmail = '', borrowerName = '') {
  const email = operatorEmail || getAuthenticatedOperatorEmail();
  const payloadItems = (itemsToBorrow || []).map(item => ({
    key: item.prod?.key || item.key || item.name || '',
    name: item.name || item.prod?.name || '',
    quantity: item.qty !== undefined ? item.qty : (item.quantity || 0),
    selectedBoxes: Array.isArray(item.selectedBoxes)
      ? item.selectedBoxes.map(entry => ({
          itemId: entry.itemId,
          boxIndex: entry.boxIndex
        }))
      : []
  }));

  return supabaseRpc('borrow_inventory', {
    p_items: payloadItems,
    p_notes: purpose || '',
    p_operator_email: email,
    p_borrower_name: borrowerName || ''
  });
}

/**
 * Invokes the atomic `return_inventory` RPC procedure.
 * @param {Array<{ borrowTx?: any, borrowTxId?: string, qtyToReturn?: number, quantity?: number, boxesToReturn?: any[], boxContents?: any[] }>} returnsToProcess
 * @param {string} [operatorEmail='']
 * @returns {Promise<any>}
 */
export async function rpcReturnInventory(returnsToProcess, operatorEmail = '') {
  const email = operatorEmail || getAuthenticatedOperatorEmail();
  const payloadReturns = (returnsToProcess || []).map(ret => ({
    borrowTxId: ret.borrowTx?.id || ret.borrowTxId,
    quantity: ret.qtyToReturn !== undefined ? ret.qtyToReturn : (ret.quantity || 0),
    boxContents: normalizeBoxContents(ret.boxesToReturn || ret.boxContents)
  }));

  return supabaseRpc('return_inventory', {
    p_returns: payloadReturns,
    p_operator_email: email
  });
}

/**
 * Invokes the atomic `stock_out_inventory` RPC procedure.
 * @param {Object} params
 * @param {string} [params.id]
 * @param {string} params.operatorName
 * @param {string} params.reason
 * @param {Array<{ item_id?: string, itemId?: string, quantity: number, item_name?: string, itemName?: string, unit?: string }>} params.items
 * @param {string} [params.timestamp]
 * @param {string} [params.operatorEmail]
 * @returns {Promise<any>}
 */
export async function rpcStockOutInventory(params) {
  const email = params.operatorEmail || getAuthenticatedOperatorEmail();
  const payloadItems = (params.items || []).map(item => ({
    item_id: item.item_id || item.itemId,
    item_name: item.item_name || item.itemName || '',
    quantity: Number(item.quantity) || 0,
    unit: item.unit || ''
  }));

  return supabaseRpc('stock_out_inventory', {
    p_id: params.id || '',
    p_operator_name: params.operatorName || '',
    p_reason: params.reason || '',
    p_items: payloadItems,
    p_timestamp: params.timestamp || new Date().toISOString(),
    p_operator_email: email
  });
}

/**
 * Reverts an entire stock-out voucher and restores stock.
 * @param {string} masterId
 * @returns {Promise<any>}
 */
export async function rpcRevertStockOut(masterId) {
  return supabaseRpc('revert_stock_out', {
    p_id: masterId
  });
}

/**
 * Updates stock-out lines and reconciles stock differences.
 * @param {Object} params
 * @returns {Promise<any>}
 */
export async function rpcUpdateStockOutInventory(params) {
  const email = params.operatorEmail || getAuthenticatedOperatorEmail();
  const payloadItems = (params.items || []).map(item => ({
    item_id: item.item_id || item.itemId,
    item_name: item.item_name || item.itemName || '',
    quantity: Number(item.quantity) || 0,
    unit: item.unit || ''
  }));

  return supabaseRpc('update_stock_out_inventory', {
    p_id: params.id,
    p_operator_name: params.operatorName || '',
    p_reason: params.reason || '',
    p_items: payloadItems,
    p_timestamp: params.timestamp || new Date().toISOString(),
    p_operator_email: email
  });
}

/**
 * Invokes the atomic `apply_stock_audit` procedure to reconcile inventory with physical counts.
 * @param {Object} params
 * @param {string} params.auditId
 * @param {boolean} [params.adjust=true]
 * @param {any[]} params.auditItems
 * @param {string} [params.operatorEmail]
 * @returns {Promise<any>}
 */
export async function rpcApplyStockAudit(params) {
  const email = params.operatorEmail || getAuthenticatedOperatorEmail();
  const adjust = params.adjust !== undefined ? params.adjust : (params.p_adjust !== undefined ? params.p_adjust : true);
  return supabaseRpc('apply_stock_audit', {
    p_audit_id: params.auditId || params.p_audit_id,
    p_adjust: adjust,
    p_audit_items: params.auditItems || params.p_audit_items || [],
    p_operator_email: email
  });
}

/**
 * Reverts a group of borrow or return transactions.
 * @param {string} timestamp
 * @param {'borrow'|'return'|'adjust'} type
 * @returns {Promise<any>}
 */
export async function rpcRevertTransactionGroup(timestamp, type) {
  return supabaseRpc('revert_transaction_group', {
    p_timestamp: timestamp,
    p_type: type
  });
}

/**
 * Reverts a single transaction by ID and restores stock.
 * @param {string} txId
 * @returns {Promise<any>}
 */
export async function rpcRevertSingleTransaction(txId) {
  return supabaseRpc('revert_single_transaction', {
    p_id: txId
  });
}

/**
 * Invokes the atomic `save_inventory_item` RPC procedure with OCC conflict check.
 * @param {import('../types/index.js').InventoryItem} item
 * @param {string|null} [expectedUpdatedAt=null]
 * @param {boolean} [preserveQuantity=false]
 * @param {string} [operatorEmail='']
 * @returns {Promise<any>}
 */
export async function rpcSaveInventoryItem(item, expectedUpdatedAt = null, preserveQuantity = false, operatorEmail = '') {
  const email = operatorEmail || getAuthenticatedOperatorEmail();
  const dbRow = toInventoryRow(item);
  return supabaseRpc('save_inventory_item', {
    p_item: dbRow,
    p_expected_updated_at: expectedUpdatedAt || null,
    p_preserve_quantity: Boolean(preserveQuantity),
    p_operator_email: email
  });
}

/**
 * Invokes the atomic `delete_inventory_item` RPC procedure.
 * @param {string} itemId
 * @param {string} [operatorEmail='']
 * @returns {Promise<any>}
 */
export async function rpcDeleteInventoryItem(itemId, operatorEmail = '') {
  const email = operatorEmail || getAuthenticatedOperatorEmail();
  return supabaseRpc('delete_inventory_item', {
    p_id: itemId,
    p_operator_email: email
  });
}

/**
 * Invokes the atomic `delete_product_lots` RPC procedure to soft-delete all lots of a product group.
 * @param {string} productKey
 * @param {string} [operatorEmail='']
 * @returns {Promise<any>}
 */
export async function rpcDeleteProductLots(productKey, operatorEmail = '') {
  const email = operatorEmail || getAuthenticatedOperatorEmail();
  return supabaseRpc('delete_product_lots', {
    p_product_key: productKey,
    p_operator_email: email
  });
}

